import {
  KLAVIYO_CATEGORY_PROPERTY,
  RECHARGE_FEATURE_MODE,
  RECHARGE_FEATURE_MODES,
  type RechargeFeatureMode,
  type RechargeSettingsSummary,
} from '@flux/shared';
import { Link } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { useKlaviyoSummary } from './api-klaviyo.js';
import { SegmentedRadioGroup, type SegmentedOption } from './recharge-parts.js';
import { useSaveRechargeSettings } from './use-save-recharge-settings.js';

const MODE_OPTIONS: readonly SegmentedOption<RechargeFeatureMode>[] = RECHARGE_FEATURE_MODES.map((mode) => ({
  value: mode,
  label: RECHARGE_FEATURE_MODE[mode].label,
}));

/** Conséquence de chaque mode, en une phrase (`06` §8.6). */
const MODE_HINTS: Readonly<Record<RechargeFeatureMode, string>> = {
  disabled: 'Aucune synchronisation de nuit. Une simulation reste possible depuis Abonnements → Klaviyo.',
  simulation: 'Chaque nuit, Flux calcule les changements sans rien écrire chez Klaviyo.',
  active: 'Chaque nuit à 4 h, Flux met à jour la catégorie d’abonné des profils Klaviyo.',
};

const MODE_HINT_ID = 'klaviyo-mode-hint';
const TEST_MODE_NOTE = 'Le mode test de Flux force la simulation (Paramètres → Avancé)';

/**
 * Carte **Synchronisation Klaviyo** de Paramètres → Recharge (`06` §11) :
 * le mode de la fonction. La clé API est celle de l'intégration Klaviyo
 * (Paramètres → Klaviyo). Un passage en Actif est confirmé, puisqu'il écrit
 * sur les profils clients.
 */
export function RechargeKlaviyoSection({ settings }: { settings: RechargeSettingsSummary }): JSX.Element {
  const klaviyo = useKlaviyoSummary();
  const [baseline, setBaseline] = useState(settings.klaviyo.mode);
  const [mode, setMode] = useState<RechargeFeatureMode>(settings.klaviyo.mode);
  const [confirming, setConfirming] = useState(false);
  const save = useSaveRechargeSettings((summary) => {
    setMode(summary.klaviyo.mode);
    setConfirming(false);
  });

  // Réglages relus depuis le serveur : on suit tant que le mode n'a pas été touché.
  if (baseline !== settings.klaviyo.mode) {
    if (mode === baseline) {
      setMode(settings.klaviyo.mode);
    }
    setBaseline(settings.klaviyo.mode);
  }

  const modeChanged = mode !== settings.klaviyo.mode;
  const keyMissing = klaviyo.data?.apiKeyConfigured === false;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!modeChanged) {
      return;
    }
    if (mode === 'active') {
      setConfirming(true);
      return;
    }
    save.mutate({ klaviyo: { mode } });
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <PageSection
        title="Synchronisation Klaviyo"
        description={`Catégorie d’abonné (${KLAVIYO_CATEGORY_PROPERTY}) sur les profils des abonnés actifs : legacy, legacy_new ou new.`}
        footer={<SaveButton isDirty={modeChanged} isLoading={save.isPending} />}
      >
        <div className="flex flex-col gap-4">
          {keyMissing ? (
            <p className="flex flex-wrap items-start gap-x-2 gap-y-1 rounded-md border border-status-gray-border bg-status-gray-bg px-3 py-2 text-sm text-status-gray-fg">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">Ajoutez la clé API Klaviyo pour que la synchronisation puisse tourner.</span>
              <Link
                to="/parametres"
                search={{ tab: 'klaviyo' }}
                className="rounded-sm font-medium underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Ouvrir Paramètres → Klaviyo
              </Link>
            </p>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <SegmentedRadioGroup
              name="klaviyo-mode"
              legend="Mode de la synchronisation"
              showLegend
              options={MODE_OPTIONS}
              value={mode}
              disabled={settings.testMode}
              describedBy={MODE_HINT_ID}
              onChange={setMode}
            />
            <p id={MODE_HINT_ID} className="text-xs leading-5 text-muted-foreground">
              {settings.testMode ? TEST_MODE_NOTE : MODE_HINTS[mode]}
            </p>
          </div>
        </div>
      </PageSection>
      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open && !save.isPending) {
            setConfirming(false);
          }
        }}
        title="Activer la synchronisation Klaviyo ?"
        description="Chaque nuit, Flux écrira la catégorie d’abonné sur les profils Klaviyo et la retirera aux clients qui n’ont plus d’abonnement actif. Lancez d’abord une simulation depuis Abonnements → Klaviyo pour vérifier les changements."
        confirmLabel="Activer"
        confirmVariant="default"
        isConfirming={save.isPending}
        onConfirm={() => {
          save.mutate({ klaviyo: { mode: 'active' } });
        }}
      />
    </form>
  );
}
